import { useState, useCallback } from 'react';
import { api } from '@/lib/api';
import { useToast } from '@/components/ui/use-toast';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Upload, FileText, X, CheckCircle, AlertCircle, Loader2, RefreshCw } from 'lucide-react';

interface UploadModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSuccess: () => void;
}

interface DuplicateInfo {
  numero: string;
  fecha_emision: string;
  valor_total: number;
  estado: string;
}

interface FileUploadStatus {
  file: File;
  status: 'pending' | 'uploading' | 'success' | 'error' | 'duplicate';
  result?: any;
  error?: string;
  xmlContent?: string;
  duplicate?: DuplicateInfo;
}

export function UploadModal({ open, onOpenChange, onSuccess }: UploadModalProps) {
  const [files, setFiles] = useState<FileUploadStatus[]>([]);
  const [isUploading, setIsUploading] = useState(false);
  const { toast } = useToast();

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const selectedFiles = Array.from(e.target.files || []);
    const xmlFiles = selectedFiles.filter(f => f.name.endsWith('.xml'));

    if (xmlFiles.length !== selectedFiles.length) {
      toast({
        title: 'Archivos ignorados',
        description: 'Solo se permiten archivos XML',
        variant: 'destructive',
      });
    }

    if (xmlFiles.length > 0) {
      setFiles(prev => [
        ...prev,
        ...xmlFiles.map(file => ({ file, status: 'pending' as const }))
      ]);
    }
  };

  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    const droppedFiles = Array.from(e.dataTransfer.files);
    const xmlFiles = droppedFiles.filter(f => f.name.endsWith('.xml'));

    if (xmlFiles.length > 0) {
      setFiles(prev => [
        ...prev,
        ...xmlFiles.map(file => ({ file, status: 'pending' as const }))
      ]);
    }
  }, []);

  const removeFile = (index: number) => {
    setFiles(prev => prev.filter((_, i) => i !== index));
  };

  const uploadSingleFile = async (index: number, replace?: boolean) => {
    setFiles(prev => prev.map((f, idx) =>
      idx === index ? { ...f, status: 'uploading' } : f
    ));

    try {
      const xmlContent = files[index].xmlContent || await files[index].file.text();
      const result = await api.uploadFactura(xmlContent, replace);

      setFiles(prev => prev.map((f, idx) =>
        idx === index ? { ...f, status: 'success', result } : f
      ));
      return 'success';
    } catch (error: any) {
      // Detectar error de duplicado (409)
      if (error?.status === 409 && error?.body?.existing) {
        const xmlContent = files[index].xmlContent || await files[index].file.text();
        setFiles(prev => prev.map((f, idx) =>
          idx === index ? {
            ...f,
            status: 'duplicate',
            xmlContent,
            duplicate: error.body.existing,
            error: error.body.message,
          } : f
        ));
        return 'duplicate';
      }

      const errorMessage = error instanceof Error ? error.message : 'Error procesando el archivo';
      setFiles(prev => prev.map((f, idx) =>
        idx === index ? { ...f, status: 'error', error: errorMessage } : f
      ));
      return 'error';
    }
  };

  const handleUpload = async () => {
    if (files.length === 0) return;

    setIsUploading(true);
    let successCount = 0;
    let errorCount = 0;

    for (let i = 0; i < files.length; i++) {
      if (files[i].status !== 'pending') continue;

      const result = await uploadSingleFile(i);
      if (result === 'success') successCount++;
      else if (result === 'error') errorCount++;
    }

    setIsUploading(false);

    if (successCount > 0) {
      toast({
        title: 'Carga completada',
        description: `${successCount} factura(s) cargada(s) correctamente${errorCount > 0 ? `, ${errorCount} con errores` : ''}`,
      });
    } else if (errorCount > 0) {
      toast({
        title: 'Error en la carga',
        description: 'No se pudo cargar ninguna factura',
        variant: 'destructive',
      });
    }
  };

  const handleReplace = async (index: number) => {
    setIsUploading(true);
    const result = await uploadSingleFile(index, true);
    setIsUploading(false);

    if (result === 'success') {
      toast({
        title: 'Factura reemplazada',
        description: 'La factura duplicada fue reemplazada correctamente',
      });
    }
  };

  const handleIgnoreDuplicate = (index: number) => {
    setFiles(prev => prev.map((f, idx) =>
      idx === index ? { ...f, status: 'error', error: 'Factura duplicada (ignorada)' } : f
    ));
  };

  const handleClose = () => {
    const hasSuccess = files.some(f => f.status === 'success');
    if (hasSuccess) {
      onSuccess();
    }
    setFiles([]);
    onOpenChange(false);
  };

  const allCompleted = files.length > 0 && files.every(f =>
    f.status === 'success' || f.status === 'error'
  );
  const hasDuplicates = files.some(f => f.status === 'duplicate');
  const pendingCount = files.filter(f => f.status === 'pending').length;

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>Cargar Facturas XML</DialogTitle>
          <DialogDescription>
            Selecciona o arrastra archivos XML de facturas electronicas
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <input
            id="file-input"
            type="file"
            accept=".xml"
            multiple
            onChange={handleFileChange}
            className="hidden"
          />

          {!allCompleted && !hasDuplicates && (
            <div
              className="border-2 border-dashed rounded-lg p-6 text-center cursor-pointer hover:border-primary/50 transition-colors"
              onDrop={handleDrop}
              onDragOver={(e) => e.preventDefault()}
              onClick={() => document.getElementById('file-input')?.click()}
            >
              <Upload className="h-8 w-8 text-muted-foreground mx-auto mb-2" />
              <p className="text-sm text-muted-foreground">
                Arrastra archivos XML aqui o haz clic para seleccionar
              </p>
              <p className="text-xs text-muted-foreground mt-1">
                Puedes seleccionar multiples archivos
              </p>
            </div>
          )}

          {files.length > 0 && (
            <div className="space-y-2 max-h-60 overflow-y-auto">
              {files.map((fileStatus, index) => (
                <div
                  key={`${fileStatus.file.name}-${index}`}
                  className={`flex items-center gap-3 p-3 rounded-lg border ${
                    fileStatus.status === 'success'
                      ? 'bg-green-50 border-green-200'
                      : fileStatus.status === 'error'
                      ? 'bg-red-50 border-red-200'
                      : fileStatus.status === 'duplicate'
                      ? 'bg-yellow-50 border-yellow-200'
                      : fileStatus.status === 'uploading'
                      ? 'bg-blue-50 border-blue-200'
                      : 'bg-muted/50'
                  }`}
                >
                  {fileStatus.status === 'success' ? (
                    <CheckCircle className="h-5 w-5 text-green-500 flex-shrink-0" />
                  ) : fileStatus.status === 'error' ? (
                    <AlertCircle className="h-5 w-5 text-red-500 flex-shrink-0" />
                  ) : fileStatus.status === 'duplicate' ? (
                    <AlertCircle className="h-5 w-5 text-yellow-500 flex-shrink-0" />
                  ) : fileStatus.status === 'uploading' ? (
                    <Loader2 className="h-5 w-5 text-blue-500 flex-shrink-0 animate-spin" />
                  ) : (
                    <FileText className="h-5 w-5 text-primary flex-shrink-0" />
                  )}

                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium truncate">{fileStatus.file.name}</p>
                    {fileStatus.status === 'success' && fileStatus.result && (
                      <p className="text-xs text-green-600">
                        {fileStatus.result.factura.numero} - {fileStatus.result.factura.proveedor}
                      </p>
                    )}
                    {fileStatus.status === 'error' && (
                      <p className="text-xs text-red-600">{fileStatus.error}</p>
                    )}
                    {fileStatus.status === 'duplicate' && fileStatus.duplicate && (
                      <div>
                        <p className="text-xs text-yellow-700">
                          Duplicada: #{fileStatus.duplicate.numero} - {fileStatus.duplicate.estado} - ${fileStatus.duplicate.valor_total.toLocaleString()}
                        </p>
                        <div className="flex gap-2 mt-1.5">
                          <Button
                            size="sm"
                            variant="outline"
                            className="h-6 text-xs px-2 border-blue-300 text-blue-700 hover:bg-blue-50"
                            onClick={() => handleReplace(index)}
                            disabled={isUploading}
                          >
                            <RefreshCw className="h-3 w-3 mr-1" />
                            Reemplazar
                          </Button>
                          <Button
                            size="sm"
                            variant="outline"
                            className="h-6 text-xs px-2"
                            onClick={() => handleIgnoreDuplicate(index)}
                            disabled={isUploading}
                          >
                            Ignorar
                          </Button>
                        </div>
                      </div>
                    )}
                    {fileStatus.status === 'pending' && (
                      <p className="text-xs text-muted-foreground">
                        {(fileStatus.file.size / 1024).toFixed(1)} KB
                      </p>
                    )}
                  </div>

                  {fileStatus.status === 'pending' && !isUploading && (
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-8 w-8"
                      onClick={() => removeFile(index)}
                    >
                      <X className="h-4 w-4" />
                    </Button>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={handleClose}>
            {allCompleted ? 'Cerrar' : 'Cancelar'}
          </Button>
          {!allCompleted && !hasDuplicates && (
            <Button onClick={handleUpload} disabled={pendingCount === 0 || isUploading}>
              {isUploading
                ? 'Procesando...'
                : `Cargar ${pendingCount} Factura${pendingCount !== 1 ? 's' : ''}`}
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
